import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { FormLayout, Translate, SubmitButton } from '@ana/shared'
import { LayoutItem } from '@ana/shared'
import PageLayout from 'components/PageLayout'
import { Formik } from 'formik'
import getAssetUrl from 'helpers/getAssetUrl'
import { usePatient } from 'providers/PatientProvider'

import * as S from './Success.styles'

export type Props = {
  variant?: string | undefined
  registered: boolean
  inClinic: boolean
  onSubmit?: () => void
}

const SuccessScreen: React.FC<Props> = ({ inClinic, variant = 'default', registered, onSubmit }) => {
  const { t } = useTranslation()
  const patient = usePatient()
  const isInternal = patient?.data?.tenant?.internal ?? false

  const layout: LayoutItem[] = [
    {
      type: 'custom',
      key: 'thankYouImage',
      xs: 12,
      render: () => {
        return (
          <S.Frame>
            <S.Image src={getAssetUrl('/images/success-illustration.png')} alt="thank you illustration" />
          </S.Frame>
        )
      },
    },
    {
      type: 'custom',
      key: 'assistantEncouragement',
      xs: 12,
      render: () => {
        if (inClinic || !isInternal) return null

        return <S.Text>{t(registered ? 'page.success.assistantLogin' : 'page.success.assistantRegister')}</S.Text>
      },
    },
  ]

  const textsByVariant: Record<string, Record<string, string>> = useMemo(
    () => ({
      default: {
        title: inClinic ? t('page.success.in_clinic.title') : t('page.success.title'),
        subtitle: inClinic ? t('page.success.in_clinic.subtitle') : '',
      },
      satisfied_filling_color: {
        title: t('page.success.inlays.title'),
        subtitle: t('page.success.inlays.subtitle'),
      },
    }),
    [inClinic, t]
  )

  const getSubmitButton = () => {
    if (inClinic)
      return (
        <SubmitButton loading={false} onClick={onSubmit} type="button">
          <Translate children="restart_anamnesis" />
        </SubmitButton>
      )

    if (!isInternal) return null

    if (registered)
      return (
        <SubmitButton loading={false} onClick={onSubmit} type="button">
          <Translate children="login_patient_account" />
        </SubmitButton>
      )

    return (
      <SubmitButton loading={false} onClick={onSubmit} type="button">
        <Translate children="open_patient_account" />
      </SubmitButton>
    )
  }

  const texts = textsByVariant[variant]

  return (
    <PageLayout unsavedChangesDialog={false} title={texts.title} subtitle={texts.subtitle}>
      <Formik initialValues={{}} onSubmit={() => {}}>
        <FormLayout items={layout} submitButton={getSubmitButton()} />
      </Formik>
    </PageLayout>
  )
}

export default SuccessScreen
